import { useEffect, useRef, useState } from 'react';
import { friendlyError } from '../utils/errors.js';

// getUserMedia rejects with a DOMException whose .name identifies the
// failure. These are the ones techs actually hit in the field; anything else
// falls back to friendlyError.
const CAMERA_ERROR_MESSAGES = {
  NotAllowedError: 'Camera access was denied. Open Settings → Safari → Camera and allow access.',
  PermissionDeniedError: 'Camera access was denied. Open Settings → Safari → Camera and allow access.',
  NotFoundError: 'No camera found on this device.',
  DevicesNotFoundError: 'No camera found on this device.',
  NotReadableError: 'Another app is using the camera. Close it and try again.',
  TrackStartError: 'Another app is using the camera. Close it and try again.',
  OverconstrainedError: "This device's camera doesn't support the settings this app needs.",
  ConstraintNotSatisfiedError: "This device's camera doesn't support the settings this app needs.",
  SecurityError: 'Camera access is blocked on this connection. Load the app over HTTPS and try again.',
  AbortError: 'Camera startup was interrupted. Try again.',
};

function cameraErrorMessage(e) {
  return CAMERA_ERROR_MESSAGES[e?.name] || friendlyError(e, 'start the camera');
}

/**
 * Full-screen camera capture for data tags.
 *
 * Visual: deep navy chrome, orange corner brackets that pulse softly while
 * scanning, snap to green when a frame is locked. Bottom controls sit on a
 * navy panel with an orange shutter (white ring + orange fill).
 *
 * The capture pipeline is unchanged: getUserMedia → canvas → JPEG dataURL
 * passed to onCapture. The "lock" animation is a 250ms visual polish that
 * fires before we hand the dataURL up to the parent — no behaviour change.
 */
export default function CameraCapture({
  onCapture,
  onCancel,
  prompt = 'Center the appliance data tag in the frame',
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [locked, setLocked] = useState(false); // brackets snap green during capture

  useEffect(() => {
    let mounted = true;
    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width:  { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
        const track = stream.getVideoTracks()[0];
        const caps = track?.getCapabilities?.();
        if (caps?.torch) setTorchSupported(true);
      } catch (e) {
        console.error('Camera error:', e);
        setError(cameraErrorMessage(e));
      }
    }
    start();
    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch (e) {
      console.warn('Torch toggle failed:', e);
    }
  }

  function capture() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

    // Snap brackets green for ~350ms before handing the image up. Gives the
    // tech a clear "got it" moment without delaying the analysis pipeline.
    setLocked(true);
    setTimeout(() => onCapture(dataUrl), 350);
  }

  // Pick an existing photo from the camera roll / photo library. The file input
  // has no `capture` attribute, so iOS opens the photo picker (not the camera).
  // Same downstream path as a live shot — the dataURL goes straight to onCapture.
  function handleFilePick(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onCapture(reader.result);
    reader.onerror = () => setError('Could not read that photo. Try another one.');
    reader.readAsDataURL(file);
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-ink-950 flex flex-col items-center justify-center p-8 text-center">
        <div className="text-fail-400 text-5xl mb-4">⚠</div>
        <h2 className="text-xl font-bold text-white mb-2">
          {error?.startsWith('Could not read') ? 'Photo Problem' : 'Camera Unavailable'}
        </h2>
        <p className="text-ink-200 mb-6 max-w-md">{error}</p>
        {/* Even with no camera, the tech can still pick a saved photo. */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-primary">
            🖼  Choose from Library
          </button>
          <button onClick={onCancel} className="btn-secondary">Go Back</button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFilePick}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950 flex flex-col">
      {/* Video viewfinder */}
      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          playsInline
          muted
        />
        <canvas ref={canvasRef} className="hidden" />

        {/* Frame overlay with corner brackets — landscape 16:7 ratio matches
            typical appliance data-tag labels (long & narrow). Brackets pulse
            softly while scanning, snap green when locked. */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className={`relative w-[82%] max-w-3xl ${
              locked ? '' : 'animate-lock-pulse'
            }`}
            style={{ aspectRatio: '16 / 7' }}
          >
            <div className={`viewfinder-corner tl ${locked ? 'locked' : ''}`} />
            <div className={`viewfinder-corner tr ${locked ? 'locked' : ''}`} />
            <div className={`viewfinder-corner bl ${locked ? 'locked' : ''}`} />
            <div className={`viewfinder-corner br ${locked ? 'locked' : ''}`} />

            {/* Animated scan line, hidden once locked */}
            {!locked && (
              <div className="absolute inset-x-3 top-0 h-0.5 bg-mra-red/80 shadow-[0_0_12px_2px_rgba(249,115,22,0.7)] animate-scan-line" />
            )}

            {/* Lock confirmation tick */}
            {locked && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-pass/20 border-2 border-pass rounded-full w-16 h-16 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 text-pass" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Top instruction bar */}
        <div className="absolute top-0 inset-x-0 p-4 bg-gradient-to-b from-ink-950/90 to-transparent">
          <p className="text-white text-center font-semibold text-tap drop-shadow-lg">
            {locked ? '✓ Got it — analyzing…' : prompt}
          </p>
        </div>

        {/* Torch toggle (when supported) */}
        {torchSupported && !locked && (
          <button
            onClick={toggleTorch}
            className={`absolute top-20 right-5 w-14 h-14 rounded-full flex items-center justify-center text-2xl transition ${
              torchOn
                ? 'bg-marginal text-ink-900'
                : 'bg-ink-900/80 text-white border border-ink-700'
            }`}
            aria-label="Toggle flashlight"
          >
            {torchOn ? '⚡' : '🔦'}
          </button>
        )}
      </div>

      {/* Bottom controls — navy panel with orange shutter */}
      <div className="bg-ink-900 border-t border-ink-700 px-6 py-6 flex items-center justify-between">
        <button
          onClick={onCancel}
          disabled={locked}
          className="text-white text-tap font-medium px-4 py-3 disabled:opacity-50"
        >
          Cancel
        </button>

        {/* Big shutter — white ring with orange fill */}
        <button
          onClick={capture}
          disabled={!ready || locked}
          className="relative w-20 h-20 rounded-full bg-white border-4 border-mra-red active:scale-95 transition disabled:opacity-50"
          aria-label="Capture photo"
        >
          <div className="absolute inset-2 rounded-full bg-mra-red" />
        </button>

        {/* Choose from photo library instead of shooting live */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={locked}
          className="w-20 flex flex-col items-center gap-1 text-white disabled:opacity-50"
          aria-label="Choose photo from library"
        >
          <span className="w-12 h-12 rounded-full bg-ink-800 border border-ink-700 flex items-center justify-center text-xl">🖼</span>
          <span className="text-xs font-medium">Library</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFilePick}
          className="hidden"
        />
      </div>
    </div>
  );
}
